/**
 * UiFeatures.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * React port of ui-features.component.{ts,html}.
 *
 * Preserves the original behaviour:
 *   • Subscription-package dropdown (Micro / Small / Enterprise) + "Find".
 *   • Left "Side Menu" column (Shipment, Pickup, … Dashboard) with a checkbox
 *     per module reflecting the module-level enabled flag.
 *   • Right "Features" column: the JSON feature tree for the selected module,
 *     rendered with checkboxes (custom FeatureTree, replacing dx-tree-view).
 *   • Loads the persisted config for the selected company size from the real
 *     feature-config API (GET) and persists changes (PATCH). Per the
 *     integration decision, NO auth header is sent; a 401/403 surfaces the
 *     original "Unauthorized user" message.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import treeDataJson from '../data/uiFeatureTree.json';
import { CompanySize, FeatureNode, FeatureTreeMap, UiFeatureConfig } from '../models/uiFeatureConfig';
import {
  loadConfig, saveConfig, isEnabled, enabledGuids, setEnabled, FeatureConfigError,
} from '../services/uiFeatureConfigService';
import { FeatureTree } from './FeatureTree';

const featureTreeMap = treeDataJson as unknown as FeatureTreeMap;

interface SideMenuItem {
  title: string;
  icon: string;
  menuGuid: string;
  shortName: string;
  selected: boolean;
}

interface SubscriptionPackage { name: string; value: CompanySize; guid: string; }

const PACKAGES: SubscriptionPackage[] = [
  { name: 'Micro',      value: 'micro',      guid: 'b1f0e2a4-6c3d-4e8a-9f21-2d7c5a0b8e11' },
  { name: 'Small',      value: 'small',      guid: 'd3b2a4c6-8e5f-4a1c-9b43-4f9e7c2d0a33' },
  { name: 'Enterprise', value: 'enterprise', guid: 'c2a1f3b5-7d4e-4f9b-8a32-3e8d6b1c9f22' },
];

const SIDE_MENU: SideMenuItem[] = [
  { title: 'Shipment',   icon: 'local_shipping', shortName: 'SHIPMENT',   menuGuid: 'b13adf14-b0c3-4d40-97c2-567b0543a018', selected: false },
  { title: 'Pickup',     icon: 'unarchive',      shortName: 'PICKUP',     menuGuid: 'f80d6db5-b113-4a71-8891-dfcba5f7c8f1', selected: false },
  { title: 'Delivery',   icon: 'archive',        shortName: 'DELIVERY',   menuGuid: 'eaa54d33-1c6f-4f3e-93b1-b6c1e1b5de21', selected: false },
  { title: 'Customs',    icon: 'archive',        shortName: 'CUSTOMS',    menuGuid: '3d99f90b-b84d-4fc8-83c2-3a4cc78bff69', selected: false },
  { title: 'Sales',      icon: 'dns',            shortName: 'SALES',      menuGuid: '649f2b17-978f-4eb2-8c8c-98fd727c3bfb', selected: false },
  { title: 'Accounting', icon: 'view_list',      shortName: 'ACCOUNTING', menuGuid: 'a137b49f-0b9b-47a5-b4fa-9f174f9e3e3f', selected: false },
  { title: 'Payment',    icon: 'local_atm',      shortName: 'PAYMENT',    menuGuid: '4d6b2bcb-b19f-41c7-8a78-cc8b2f69f5ae', selected: false },
  { title: 'Actor',      icon: 'group',          shortName: 'ACTOR',      menuGuid: 'e02cbfde-7f49-408e-8043-d978dcb04d7e', selected: false },
  { title: 'Settings',   icon: 'settings',       shortName: 'SETTINGS',   menuGuid: '94e58fa9-b234-41d6-a229-f3877e204c09', selected: false },
  { title: 'Dashboard',  icon: 'dashboard',      shortName: 'DASHBOARD',  menuGuid: '22c48563-d95d-5d83-9d28-eb0c6f31e138', selected: false },
];

const moduleTree = (shortName: string): FeatureNode[] => {
  const v = featureTreeMap[shortName];
  return Array.isArray(v) ? (v as FeatureNode[]) : [];
};

/** Collect every guid (module, submenu, feature) under a module's tree. */
function collectGuids(nodes: FeatureNode[] | undefined): string[] {
  const guids: string[] = [];
  const walk = (list?: FeatureNode[]) => {
    if (!list) return;
    for (const n of list) {
      if (n.guid) guids.push(n.guid);
      if (n.items && n.items.length) walk(n.items);
    }
  };
  walk(nodes);
  return guids;
}

/** Return a copy of the tree with each node's `selected` flag set from the guid set. */
function markSelectedNodes(nodes: FeatureNode[] | undefined, selectedGuids: Set<string>): FeatureNode[] {
  if (!nodes) return [];
  return nodes.map(node => ({
    ...node,
    selected: selectedGuids.has(node.guid),
    items: node.items && node.items.length ? markSelectedNodes(node.items, selectedGuids) : node.items,
  }));
}

type Msg = { type: 'error' | 'success'; text: string } | null;

export const UiFeatures: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<CompanySize | ''>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showTree, setShowTree] = useState(false);
  const [message, setMessage] = useState<Msg>(null);

  const [menuItems, setMenuItems] = useState<SideMenuItem[]>(() => SIDE_MENU.map(m => ({ ...m })));
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number | null>(null);
  const [hoverMenuIndex, setHoverMenuIndex] = useState<number | null>(null);
  const [selectedMenuShortName, setSelectedMenuShortName] = useState('');
  const [selectedTreeItems, setSelectedTreeItems] = useState<FeatureNode[]>([]);

  // Mutable refs (mirror the Angular component's mutable fields) used inside
  // async save handlers so they never read stale state.
  const configRef = useRef<UiFeatureConfig | null>(null);
  const selectedGuidsRef = useRef<Set<string>>(new Set());
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  const flash = useCallback((type: 'error' | 'success', text: string) => {
    setMessage({ type, text });
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMessage(null), 4000);
  }, []);

  const handleLoadError = useCallback((err: any) => {
    const status = err instanceof FeatureConfigError ? err.status : undefined;
    const code = err instanceof FeatureConfigError ? err.code : undefined;
    if (code === 'UNAUTHORIZED' || status === 401 || status === 403) {
      flash('error', 'Unauthorized user');
    } else {
      flash('error', err?.message || 'Failed to load configuration');
    }
  }, [flash]);

  // Open a module → render its feature tree on the right, pre-marked from config.
  const openMenu = useCallback((menu: SideMenuItem, index: number) => {
    setSelectedMenuIndex(index);
    setHoverMenuIndex(null);
    setSelectedMenuShortName(menu.shortName);
    setSelectedTreeItems(markSelectedNodes(moduleTree(menu.shortName), selectedGuidsRef.current));
  }, []);

  // Load config for the selected size and sync side-menu + guid set.
  const loadConfigForSelected = useCallback(async () => {
    const size = selectedPackage as CompanySize;
    const cfg = await loadConfig(size);
    configRef.current = cfg;
    selectedGuidsRef.current = new Set(enabledGuids(cfg));
    setMenuItems(prev => prev.map(m => ({ ...m, selected: isEnabled(cfg, m.menuGuid) })));
  }, [selectedPackage]);

  // "Find" — load the persisted config and reflect it in the UI.
  const onShow = useCallback(async () => {
    if (!selectedPackage) {
      setErrorMessage('Please select a subscription package.');
      setShowTree(false);
      return;
    }
    setErrorMessage('');
    setShowTree(true);
    try {
      await loadConfigForSelected();
      openMenu(menuItems[0], 0);
    } catch (err) {
      setShowTree(false);
      handleLoadError(err);
    }
  }, [selectedPackage, loadConfigForSelected, openMenu, menuItems, handleLoadError]);

  // Switching company size while the tree is visible reloads immediately.
  const onPackageChanged = useCallback(async (value: CompanySize) => {
    setSelectedPackage(value);
    setErrorMessage('');
    if (showTree && value) {
      try {
        // loadConfigForSelected reads selectedPackage; call inline with new value.
        const cfg = await loadConfig(value);
        configRef.current = cfg;
        selectedGuidsRef.current = new Set(enabledGuids(cfg));
        setMenuItems(prev => prev.map(m => ({ ...m, selected: isEnabled(cfg, m.menuGuid) })));
        const idx = SIDE_MENU.findIndex(m => m.shortName === selectedMenuShortName);
        const target = idx >= 0 ? idx : 0;
        openMenu(SIDE_MENU[target], target);
      } catch (err) {
        handleLoadError(err);
      }
    }
  }, [showTree, selectedMenuShortName, openMenu, handleLoadError]);

  // Side-menu module checkbox toggled.
  const onMenuCheckboxChanged = useCallback(async (item: SideMenuItem, index: number, checked: boolean) => {
    setMenuItems(prev => prev.map((m, i) => (i === index ? { ...m, selected: checked } : m)));

    const cfg = configRef.current;
    if (cfg) {
      setEnabled(cfg, item.menuGuid, checked);
      if (checked) selectedGuidsRef.current.add(item.menuGuid);
      else selectedGuidsRef.current.delete(item.menuGuid);
      try {
        await saveConfig(cfg);
      } catch (err) {
        handleLoadError(err);
      }
    }
    if (checked) openMenu(item, index);
  }, [openMenu, handleLoadError]);

  // Feature tree selection changed (user toggled a checkbox in the right column).
  const onTreeSelectionChange = useCallback(async (selectedKeys: string[]) => {
    const cfg = configRef.current;
    if (!cfg || !selectedMenuShortName) return;

    const moduleGuids = collectGuids(moduleTree(selectedMenuShortName));
    const selectedSet = new Set(selectedKeys);
    moduleGuids.forEach(g => {
      const enabled = selectedSet.has(g);
      setEnabled(cfg, g, enabled);
      if (enabled) selectedGuidsRef.current.add(g);
      else selectedGuidsRef.current.delete(g);
    });

    try {
      await saveConfig(cfg);
    } catch (err) {
      handleLoadError(err);
    }
  }, [selectedMenuShortName, handleLoadError]);

  return (
    <div className="uif-page">
      {message && (
        <div className={`lp-msg lp-msg--${message.type}`}>
          <span>{message.text}</span>
          <button className="lp-msg__close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Page header */}
      <div className="uif-page__head">
        <h1 className="uif-page__title">UI Features</h1>
        <p className="uif-page__sub">Enable or disable application UI features per subscription package.</p>
      </div>

      {/* Panel */}
      <div className="ui-feature-panel">
        <div className="tree_view_container">

          {/* Dropdown row: Subscription Package + Find */}
          <div className="tree_dropdown_section">
            <div className="tree_dropdown_container">
              <span className="tree_dropdown_title">Subscription Package</span>
              <select
                className="feature_select"
                value={selectedPackage}
                onChange={e => onPackageChanged(e.target.value as CompanySize)}
              >
                <option value="" disabled>Select Package</option>
                {PACKAGES.map(p => <option key={p.value} value={p.value}>{p.name}</option>)}
              </select>
            </div>
            <div className="tree_dropdown_container_2">
              <span className="lp_btn_blue" onClick={onShow}>Find</span>
            </div>
          </div>

          {errorMessage && <div className="uif-error">{errorMessage}</div>}

          {/* Tree structure (side menu + feature tree) */}
          {showTree && (
            <div className="panel_content height_68vh">
              <div className="laptop-container">
                <div className="panel_inner_content">
                  <div className="feature_side_container">

                    {/* Side Menu (left) */}
                    <div className="assigned_side_container">
                      <div className="menu_container">
                        <div className="menu_title_container">
                          <div className="agency_title">
                            <p className="blue_color_fourth capitalize">Side Menu</p>
                          </div>
                          <hr className="row_line" />
                        </div>

                        <div className="agency_row_container">
                          <div className="agency_rows">
                            {menuItems.map((item, i) => (
                              <div
                                key={item.menuGuid}
                                className={`agency_row_child ${selectedMenuIndex === i ? 'menu_selected' : 'menu_not_selected'} ${hoverMenuIndex === i ? 'menu_hover' : ''}`}
                                onClick={() => openMenu(item, i)}
                                onMouseEnter={() => setHoverMenuIndex(i)}
                                onMouseLeave={() => setHoverMenuIndex(null)}
                              >
                                <div className="sidemenu_flex">
                                  <span className="sidemenu_checkbox_wrapper">
                                    <input
                                      type="checkbox"
                                      className="user_role_checkbox"
                                      checked={item.selected}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => onMenuCheckboxChanged(item, i, e.target.checked)}
                                    />
                                  </span>
                                  <i className="material-icons md-24 sidemenu_icon">{item.icon}</i>
                                  <span className="agency_name">{item.title}</span>
                                  {selectedMenuIndex === i && (
                                    <span className="sidemenu_selected_arrow">
                                      <i className="material-icons md-24">keyboard_arrow_right</i>
                                    </span>
                                  )}
                                </div>
                                <div className="sidemenu_divider" />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feature tree (right) */}
                    <div className="assigned_feature_container">
                      <div className="feature_container">
                        <div className="feature_title_container">
                          <div className="agency_title">
                            <p className="blue_color_fourth capitalize title-text">Features</p>
                          </div>
                        </div>
                        <hr className="row_line" />

                        {selectedTreeItems.length > 0 && (
                          <div className="tree-view-container">
                            <FeatureTree
                              nodes={selectedTreeItems}
                              resetKey={`${selectedMenuShortName}:${selectedPackage}`}
                              onSelectionChange={onTreeSelectionChange}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
